import React, { useState } from 'react';
import styled from 'styled-components';

interface Credit {
    name: string;
    role: string;
}

interface VideoInfoProps {
    title: string;
    description: string;
    credits: Credit[];
}

const InfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.lg};
    color: ${({ theme }) => theme.colors.text};
`;

const Title = styled.h2`
    font-size: ${({ theme }) => theme.fontSizes.header};
    color: ${({ theme }) => theme.colors.primary};
    margin: 0;
    font-weight: ${({ theme }) => theme.fontWeights.bold};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.fontSizes.headerMobile};
    }
`;

const Description = styled.p`
    font-size: ${({ theme }) => theme.fontSizes.paragraph};
    line-height: 1.6;
    margin: 0;
    color: ${({ theme }) => theme.colors.text};
`;

const CreditsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.sm};
`;

const CreditsToggle = styled.button<{ $isOpen: boolean }>`
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.primary};
    font-size: ${({ theme }) => theme.fontSizes.paragraph};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    cursor: pointer;
    padding: 0;
    text-align: left;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xs};
    transition: color 0.2s ease;

    &:hover {
        color: ${({ theme }) => theme.colors.primaryDark};
    }

    &::after {
        content: '${({ $isOpen }) => ($isOpen ? '▼' : '▶')}';
        font-size: 0.8em;
    }
`;

const CreditsList = styled.div<{ $isOpen: boolean }>`
    display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xs};
    padding-left: ${({ theme }) => theme.spacing.md};
`;

const CreditItem = styled.div`
    font-size: ${({ theme }) => theme.fontSizes.paragraph};
    line-height: 1.6;
`;

const CreditName = styled.span`
    color: ${({ theme }) => theme.colors.primary};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const CreditRole = styled.span`
    color: ${({ theme }) => theme.colors.text};
`;

export const VideoInfo: React.FC<VideoInfoProps> = ({ title, description, credits }) => {
    const [creditsOpen, setCreditsOpen] = useState(false);

    return (
        <InfoContainer>
            <Title>{title}</Title>
            <Description>{description}</Description>

            <CreditsSection>
                <CreditsToggle onClick={() => setCreditsOpen(!creditsOpen)} $isOpen={creditsOpen}>
                    Video Credits
                </CreditsToggle>
                <CreditsList $isOpen={creditsOpen}>
                    {credits.map((credit, index) => (
                        <CreditItem key={index}>
                            <CreditName>{credit.name}</CreditName>
                            <CreditRole> – {credit.role}</CreditRole>
                        </CreditItem>
                    ))}
                </CreditsList>
            </CreditsSection>
        </InfoContainer>
    );
};
